import { createLogic } from 'redux-logic'
import { oneGraph } from '@chegg-wt/core/api/oneGraph'
import { AppState, LogicDeps } from '@chegg-wt/core/state'
import getFeatureEditor from '@chegg-wt/core/utils/paraphraser/getEditor'
import { Editor } from '@tiptap/react'
import {
  clearToast,
  reviewPaper,
  setActiveFeedbackTab,
  setFeedbackReason,
  setPaperId,
  setPaperTitle,
  setWritingAssitantPaperStatus,
  updatePaperHandler,
} from '@chegg-wt/core/state/paraphraser/actions'
import pusherManager from '@chegg-wt/core/utils/paraphraser/pusherManager'
import { updatePaper } from '@chegg-wt/core/utils/paraphraser/paperIndexedDb'
import fullPaperChecker from '@chegg-wt/core/utils/paraphraser/pusherDataHandlers/fullPaperCheckHandler'
import { CHANNEL_PROOFREAD_FULL_ANALYZE_EVENT } from '@chegg-wt/core/constants/pusher'
import history from '@chegg-wt/core/utils/paraphraser/history/history'
import {
  addUnderlineEditorText,
  createGuestUserPaper,
  getCurrentDateInMMDDYY,
  mapSentencesToSentenceOffsets,
  storeProofReaderPapersInLocal,
} from '../../utils/paraphraser/helpers'
import { navigated } from '../app/actions'
import { emitComponentViewEvent } from '../instrumentation/actions'
import { componentOrigins } from '../instrumentation/rio/clickstreamComponentViews'
import { ActionTypes } from './constants'
import { setFeedbacks, setSentenceOffsets } from './actions'
import { SentenceOffset, WritingAssistantPaperStatus } from './types'
import { handlePusherEventData } from './utils'
import {
  feedbacksSelector,
  paperIdSelector,
  paperRequiresRecheckSelector,
  paperStatusSelector,
  paperTitleSelector,
  sentenceOffsetsSelector,
  totalFeedbackCountSelector,
} from './selectors'

export const reviewPaperLogic = createLogic<
  AppState,
  ReturnType<typeof reviewPaper>['payload'],
  any,
  LogicDeps
>({
  type: [ActionTypes.RUN_PROOFREAD_CHECKS],
  async process({ action, getState }, dispatch, done) {
    history.clear()
    dispatch(clearToast())
    dispatch(setWritingAssitantPaperStatus(WritingAssistantPaperStatus.REVIEW))
    fullPaperChecker.startFullPaperCheck(dispatch, done)
    const editor: Editor = getFeatureEditor()
    const editorText = editor.getText()
    try {
      await pusherManager.subscribe(
        CHANNEL_PROOFREAD_FULL_ANALYZE_EVENT,
        handlePusherEventData
      )
    } catch {
      fullPaperChecker.notifyErrorInPaperCheckCall(dispatch)
      return
    }
    const traceId = await pusherManager.getTraceId()
    const { routes } = action.payload

    const state = getState()
    let paperId = paperIdSelector(state)
    let title = paperTitleSelector(state)
    const paperContent = {
      editorJSON: editor.getJSON(),
    }

    // We will only add title when creating paper first time
    if (!title && !paperId) {
      title = `Paper - ${getCurrentDateInMMDDYY()}`
      dispatch(setPaperTitle(title))
    }

    // If paperId is present we will just send paperId so that backend not save the paper
    // eslint-disable-next-line no-nested-ternary
    const paperData = paperId
      ? { paperId }
      : storeProofReaderPapersInLocal()
      ? {}
      : { paperContent, title }

    paperId = await createGuestUserPaper({
      paperId,
      title,
      paperContent,
      status: WritingAssistantPaperStatus.REVIEW,
    })
    if (paperId) {
      dispatch(setPaperId(paperId))
      dispatch(navigated(routes.toWritingAssistant({ paperId })))
      paperData.paperId = paperId // Adding this paperId in graphql call so that we don't save paper
    }

    const response = await oneGraph
      .paperCheck({
        request: {
          traceId,
          initiatedAt: new Date().toISOString(),
          paperText: editorText,
          ...paperData,
        },
      })
      .catch(() => {
        fullPaperChecker.notifyErrorInPaperCheckCall(dispatch)
      })

    if (!response) return

    // We get new paper created by backend server
    if (!paperId && response.paperId) {
      paperId = response.paperId
      setPaperId(paperId)
      dispatch(navigated(routes.toWritingAssistant({ paperId })))
    }
    const mappedSentences: SentenceOffset[] = mapSentencesToSentenceOffsets(
      response.sentences,
      editorText
    )
    dispatch(setSentenceOffsets(mappedSentences))
    fullPaperChecker.notifyGetSentences(mappedSentences, dispatch)
  },
})

export const deleteFeedbackLogic = createLogic<
  AppState,
  { feedbackId: string },
  any,
  LogicDeps
>({
  type: [ActionTypes.DELETE_FEEDBACK],
  async process({ getState, action }, dispatch, done) {
    const state = getState()
    const { feedbackId } = action.payload
    const feedbacks = feedbacksSelector(state)
    const filteredFeedbacks = feedbacks.filter((feedback) => feedback.id !== feedbackId)
    dispatch(setFeedbacks(filteredFeedbacks))
    done()
  },
})

export const setPaperRequiresRecheckLogic = createLogic<
  AppState,
  { requiresReCheck: boolean },
  any,
  LogicDeps
>({
  type: [ActionTypes.SET_PAPER_REQUIRES_RECHECK],
  validate({ getState, action }, allow, reject) {
    const state = getState()
    const isRequiresRecheck = paperRequiresRecheckSelector(state)
    if (isRequiresRecheck === action.payload.requiresReCheck) {
      reject(action)
    }
    allow(action)
  },
  async process({ getState, action }, dispatch, done) {
    const state = getState()
    const sentenceOffset = sentenceOffsetsSelector(state)
    const { requiresReCheck } = action.payload
    if (requiresReCheck === true && sentenceOffset.length > 0) {
      dispatch(
        emitComponentViewEvent({
          origin: componentOrigins.proofReaderReEnableReviewButton,
        })
      )
    }
    done()
  },
})

export const updatePaperHandlerLogic = createLogic<AppState, {}, any, LogicDeps>({
  type: [ActionTypes.UPDATE_PAPER_HANDLER],
  async process({ getState }, dispatch, done) {
    const editor: Editor = getFeatureEditor()
    const state = getState()
    const paperId = paperIdSelector(state)
    const title = paperTitleSelector(state)
    const status = paperStatusSelector(state)
    const feedBackList = feedbacksSelector(state)
    const sentences = sentenceOffsetsSelector(state)
    const requiresRecheck = paperRequiresRecheckSelector(state)
    const totalFeedbackCount = totalFeedbackCountSelector(state)

    const dbSentences = sentences.map((sentence) => ({
      id: sentence.id,
      offset: sentence.offset,
      length: sentence.length,
    }))

    if (storeProofReaderPapersInLocal()) {
      updatePaper(paperId, {
        title,
        status,
        paperContent: { editorJSON: editor.getJSON() },
        feedbackState: {
          feedbackList: feedBackList,
          sentences: dbSentences,
          totalFeedbackCount,
        },
        requiresRecheck,
      })
    }

    done()
  },
})

export const setActiveFeedbackTabLogic = createLogic<
  AppState,
  ReturnType<typeof setActiveFeedbackTab>['payload'],
  any,
  LogicDeps
>({
  type: [ActionTypes.SET_ACTIVE_FEEDBACK_TAB],
  async process({ action, getState }) {
    const state = getState()
    const { activeFeedbackTab } = action.payload
    const feedbacks = feedbacksSelector(state)
    const sentenceOffsets = sentenceOffsetsSelector(state)
    if (activeFeedbackTab >= 0 && feedbacks.length > 0 && sentenceOffsets.length > 0) {
      addUnderlineEditorText(feedbacks, sentenceOffsets, activeFeedbackTab)
    }
  },
})

export const setFeedbackReasonLogic = createLogic<
  AppState,
  ReturnType<typeof setFeedbackReason>['payload'],
  any,
  LogicDeps
>({
  type: [ActionTypes.SET_FEEDBACK_REASON],
  process(_, dispatch, done) {
    dispatch(updatePaperHandler())
    done()
  },
})
